import React from 'react';

import { CopyToClipboard } from '@/components/CopyToClipboard';
import { formatValue } from '@/sections/poe/processors/wiz/parsers';
import type {
  WizParsedData,
  WizRenderContext,
} from '@/sections/poe/processors/wiz/types';
import { formatKey } from '@/sections/poe/utils/formatKey';
import { isValidExternalURL } from '@/utils/url.util';

export function renderGenericResource(
  parsed: WizParsedData,
  ctx: WizRenderContext
): void {
  const { resourceData, wizId, isSimpleFormat, isSubscriptionFormat } = parsed;
  const { sections, tempTabs, mainTabKeys } = ctx;

  if (
    resourceData &&
    !isSimpleFormat &&
    !isSubscriptionFormat &&
    mainTabKeys.includes('ATTRIBUTES')
  ) {
    sections['wiz_resource'] = (
      <div className="space-y-4">
        {wizId && (
          <div className="border-chariot-stroke rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <h4 className="text-chariot-text-primary font-semibold">
                Cloud Resource
              </h4>
              <span className="border-chariot-stroke bg-chariot-background-secondary text-chariot-text-primary inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium">
                Wiz ID: {wizId}
              </span>
            </div>
          </div>
        )}

        {resourceData?.subscription && (
          <div className="border-chariot-stroke rounded-lg border">
            <div className="border-chariot-stroke border-b px-4 py-3">
              <h5 className="text-chariot-text-primary font-semibold">
                Subscription Details
              </h5>
            </div>
            <div className="space-y-3 p-4">
              {(() => {
                const subscription = resourceData.subscription;
                if (typeof subscription === 'string') {
                  const lines = subscription
                    .split('\n')
                    .filter(line => line.trim());
                  const parsed: Record<string, string> = {};

                  for (const line of lines) {
                    const normalized = line.replace(/^•\s*/, '');
                    if (normalized.includes(':')) {
                      const [key, ...valueParts] = normalized.split(':');
                      const value = valueParts.join(':').trim();
                      if (key && value) {
                        parsed[key.trim()] = value;
                      }
                    }
                  }

                  return Object.entries(parsed).length > 0 ? (
                    <>
                      {Object.entries(parsed).map(([key, value]) => (
                        <div
                          key={key}
                          className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0"
                        >
                          <span className="text-chariot-text-secondary text-sm font-medium">
                            {formatKey(key)}:
                          </span>
                          <div className="flex max-w-md items-center gap-2">
                            <span className="text-chariot-text-primary text-right text-sm break-words">
                              {key.toLowerCase().includes('url') &&
                              isValidExternalURL(value) ? (
                                <a
                                  href={value}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-chariot-brand-primary hover:underline"
                                >
                                  {value}
                                </a>
                              ) : (
                                <span>{value}</span>
                              )}
                            </span>
                            <CopyToClipboard
                              textToCopy={value}
                              alwaysShowCopyIcon
                            />
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="text-chariot-text-secondary text-sm">
                      {subscription}
                    </div>
                  );
                } else {
                  return (
                    <pre className="bg-chariot-background-secondary text-chariot-text-secondary overflow-x-auto rounded p-3 text-xs whitespace-pre-wrap">
                      {JSON.stringify(subscription, null, 2)}
                    </pre>
                  );
                }
              })()}
            </div>
          </div>
        )}

        {resourceData &&
          Object.keys(resourceData).filter(
            key => key !== 'subscription' && key !== 'wiz_id'
          ).length > 0 && (
            <div className="border-chariot-stroke rounded-lg border">
              <div className="border-chariot-stroke border-b px-4 py-2">
                <h5 className="text-chariot-text-primary font-semibold">
                  Additional Properties
                </h5>
              </div>
              <div className="space-y-3 p-4">
                {resourceData &&
                  Object.entries(resourceData)
                    .filter(
                      ([key]) => key !== 'subscription' && key !== 'wiz_id'
                    )
                    .map(([key, value]) => {
                      if (typeof value === 'object' && value !== null) {
                        return Object.entries(value).map(
                          ([subKey, subValue]) => (
                            <div
                              key={`${key}-${subKey}`}
                              className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0"
                            >
                              <span className="text-chariot-text-secondary font-medium">
                                {formatKey(subKey)}:
                              </span>
                              <div className="flex max-w-md items-center gap-2">
                                <span className="text-chariot-text-primary text-right">
                                  {String(subValue)}
                                </span>
                                <CopyToClipboard
                                  textToCopy={String(subValue)}
                                  alwaysShowCopyIcon
                                />
                              </div>
                            </div>
                          )
                        );
                      } else if (
                        typeof value === 'string' &&
                        (value.includes('\n') || value.includes('•'))
                      ) {
                        const lines = value
                          .split('\n')
                          .filter(line => line.trim());
                        const hasStructuredData = lines.some(
                          line => line.includes(':') || line.startsWith('•')
                        );

                        if (hasStructuredData) {
                          const parsed: Record<string, string> = {};
                          for (const line of lines) {
                            if (line.includes(':')) {
                              const [lineKey, ...valueParts] = line.split(':');
                              const lineValue = valueParts.join(':').trim();
                              if (lineKey && lineValue) {
                                parsed[lineKey.trim().replace(/^•\s*/, '')] =
                                  lineValue;
                              }
                            }
                          }

                          if (Object.entries(parsed).length > 0) {
                            return Object.entries(parsed).map(
                              ([lineKey, lineValue]) => (
                                <div
                                  key={`${key}-${lineKey}`}
                                  className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0"
                                >
                                  <span className="text-chariot-text-secondary font-medium">
                                    {formatKey(lineKey)}:
                                  </span>
                                  <div className="flex max-w-md items-center gap-2">
                                    <span className="text-chariot-text-primary overflow-hidden text-right">
                                      {lineKey.toLowerCase().includes('url') &&
                                      isValidExternalURL(lineValue) ? (
                                        <a
                                          href={lineValue}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-chariot-brand-primary break-all hover:underline"
                                        >
                                          {lineValue}
                                        </a>
                                      ) : (
                                        <span>{lineValue}</span>
                                      )}
                                    </span>
                                    <CopyToClipboard
                                      textToCopy={lineValue}
                                      alwaysShowCopyIcon
                                    />
                                  </div>
                                </div>
                              )
                            );
                          }
                        }
                        return (
                          <div
                            key={key}
                            className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0"
                          >
                            <span className="text-chariot-text-secondary font-medium">
                              {formatKey(key)}:
                            </span>
                            <div className="flex max-w-md items-center gap-2">
                              <span className="text-chariot-text-primary text-right whitespace-pre-line">
                                {value}
                              </span>
                              <CopyToClipboard
                                textToCopy={value}
                                alwaysShowCopyIcon
                              />
                            </div>
                          </div>
                        );
                      } else {
                        return (
                          <div
                            key={key}
                            className="border-chariot-stroke flex items-center justify-between border-b py-2 last:border-b-0"
                          >
                            <span className="text-chariot-text-secondary font-medium">
                              {formatKey(key)}:
                            </span>
                            <div className="flex max-w-md items-center gap-2 text-right">
                              {formatValue(value, key)}
                              <CopyToClipboard
                                textToCopy={String(value)}
                                alwaysShowCopyIcon
                              />
                            </div>
                          </div>
                        );
                      }
                    })}
              </div>
            </div>
          )}
      </div>
    );
    tempTabs['ATTRIBUTES'].push({
      label: 'Resource Details',
      value: 'wiz_resource',
    });
  }
}
